import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, Clock, CheckCircle, XCircle, ChevronRight, Building2, Star, TrendingUp, Gift, Copy, Share2, Sparkles, Award, Zap, Wallet as WalletIcon, CreditCard, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import MelaninPurchase from '@/components/MelaninPurchase';
import MelaninAccount from '@/components/MelaninAccount';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const TransactionStatusIcon = ({ status }) => {
  switch (status) {
    case 'confirmed':
      return <CheckCircle className="w-4 h-4 text-green-400" />;
    case 'pending':
      return <Clock className="w-4 h-4 text-yellow-400" />;
    case 'forfeited':
      return <XCircle className="w-4 h-4 text-red-400" />;
    default:
      return null;
  }
};

const TransactionSourceIcon = ({ source, type }) => {
  if (type === 'spend') {
    return <ArrowDownRight className="w-4 h-4" />;
  }
  switch (source) {
    case 'business_submission':
      return <Building2 className="w-4 h-4" />;
    case 'review':
      return <Star className="w-4 h-4" />;
    case 'referral':
      return <Gift className="w-4 h-4" />;
    case 'boost':
      return <Zap className="w-4 h-4" />;
    case 'badge':
      return <Award className="w-4 h-4" />;
    default:
      return <ArrowUpRight className="w-4 h-4" />;
  }
};

export default function Wallet({ onBalanceChange }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });
  const [earningsBySource, setEarningsBySource] = useState({});
  const [referralStats, setReferralStats] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [codeToApply, setCodeToApply] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // overview, account, buy, shop, referrals

  const refreshWallet = async () => {
    try {
      const summaryRes = await fetch(`${API}/wallet/summary`, {
        credentials: 'include'
      });
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        const walletData = data.wallet || data;
        setWallet({
          balance: walletData.balance,
          pending_balance: walletData.pending_balance,
          total_earned: walletData.total_earned,
          total_spent: walletData.total_spent || 0
        });
        setReferralCode(walletData.referral_code || '');
        
        // Also refresh transactions and earnings
        setTransactions(data.recent_transactions || []);
        setEarningsBySource(data.earnings_by_source || {});
        
        // Notify parent of balance change
        if (onBalanceChange) {
          const totalBalance = (walletData.balance || 0) + (walletData.pending_balance || 0);
          onBalanceChange(totalBalance);
        }
      }
    } catch (error) {
      console.error('Error refreshing wallet:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch wallet summary
        const summaryRes = await fetch(`${API}/wallet/summary`, {
          credentials: 'include'
        });
        
        if (summaryRes.ok) {
          const data = await summaryRes.json();
          // Wallet data is nested under 'wallet' key in summary response
          const walletData = data.wallet || data;
          const totalBalance = (walletData.balance || 0) + (walletData.pending_balance || 0);
          setWallet({
            balance: walletData.balance,
            pending_balance: walletData.pending_balance,
            total_earned: walletData.total_earned,
            total_spent: walletData.total_spent || 0
          });
          setTransactions(data.recent_transactions || []);
          setEarningsBySource(data.earnings_by_source || {});
          setReferralCode(walletData.referral_code || '');
          
          // Notify parent of balance change
          if (onBalanceChange) {
            onBalanceChange(totalBalance);
          }
        }

        // Fetch referral stats
        const refRes = await fetch(`${API}/referral/stats`, {
          credentials: 'include'
        });
        if (refRes.ok) {
          const refData = await refRes.json();
          setReferralStats(refData);
        }
      } catch (error) {
        console.error('Error fetching wallet:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchTransactions = async (page = 1) => {
    try {
      const response = await fetch(`${API}/wallet/transactions?page=${page}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions);
        setPagination({ page: data.page, pages: data.pages });
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/auth?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const applyReferralCode = async () => {
    if (!codeToApply.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplyingCode(true);
    try {
      const response = await fetch(`${API}/referral/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ referral_code: codeToApply.trim() })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setCodeToApply('');
        // Refresh wallet
        const summaryRes = await fetch(`${API}/wallet/summary`, { credentials: 'include' });
        if (summaryRes.ok) {
          const summaryData = await summaryRes.json();
          setWallet({
            balance: summaryData.balance,
            pending_balance: summaryData.pending_balance,
            total_earned: summaryData.total_earned,
            total_spent: summaryData.total_spent || 0
          });
        }
      } else {
        toast.error(data.detail || 'Failed to apply code');
      }
    } catch (error) {
      toast.error('Failed to apply referral code');
    } finally {
      setApplyingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-40 skeleton rounded-xl" />
        <div className="h-64 skeleton rounded-xl" />
      </div>
    );
  }

  const sourceLabels = {
    business_submission: 'Business Submissions',
    review: 'Reviews',
    referral: 'Referrals',
    boost: 'Boosts',
    badge: 'Badges'
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
          Rewards
        </p>
        <h1 className="text-3xl font-bold text-white tracking-tight">
          Melanin Wallet
        </h1>
      </div>

      {/* Balance Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border border-zinc-800 p-8"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        </div>

        <div className="relative">
          {/* Melanin Icon */}
          <div className="flex items-center gap-3 mb-6">
            <img 
              src={MELANIN_ICON} 
              alt="Melanin" 
              className="w-12 h-12 rounded-full"
            />
            <span className="font-mono text-sm text-zinc-500">MELANIN BALANCE</span>
          </div>

          {/* Main Balance */}
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-5xl font-bold text-white">
              {wallet?.balance?.toFixed(2) || '0.00'}
            </span>
            <span className="text-xl text-zinc-500">MEL</span>
          </div>

          {/* Pending Balance */}
          {wallet?.pending_balance > 0 && (
            <div className="flex items-center gap-2 text-yellow-400 mb-6">
              <Clock className="w-4 h-4" />
              <span className="text-sm">
                +{wallet.pending_balance.toFixed(2)} MEL pending
              </span>
            </div>
          )}

          {/* Stats Row */}
          <div className="flex items-center gap-8 pt-6 border-t border-zinc-800">
            <div>
              <p className="text-zinc-500 text-sm mb-1">Total Earned</p>
              <p className="text-xl font-semibold text-green-400">
                +{wallet?.total_earned?.toFixed(2) || '0.00'} MEL
              </p>
            </div>
            <div>
              <p className="text-zinc-500 text-sm mb-1">Total Spent</p>
              <p className="text-xl font-semibold text-red-400">
                -{wallet?.total_spent?.toFixed(2) || '0.00'} MEL
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: TrendingUp },
          { id: 'account', label: 'Melanin Account', icon: DollarSign },
          { id: 'buy', label: 'Buy', icon: CreditCard },
          { id: 'shop', label: 'Shop', icon: Sparkles },
          { id: 'referrals', label: 'Referrals', icon: Gift }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white text-black'
                : 'bg-zinc-900 text-zinc-400 hover:text-white'
            }`}
            data-testid={`wallet-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* How to Earn */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-6"
          >
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              How to Earn Melanin
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-white">Submit a Business</p>
                  <p className="text-zinc-500 text-sm">+0.50 MEL (on verification)</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="font-medium text-white">Write a Review</p>
                  <p className="text-zinc-500 text-sm">+0.10 MEL instantly</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="font-medium text-white">Refer a Friend</p>
                  <p className="text-zinc-500 text-sm">+1.00 MEL per referral</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Earnings Breakdown */}
          {Object.keys(earningsBySource).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-xl p-6"
            >
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Earnings Breakdown
              </h2>
              
              <div className="space-y-3">
                {Object.entries(earningsBySource).map(([source, amount]) => (
                  <div key={source} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TransactionSourceIcon source={source} type="earn" />
                      <span className="text-zinc-300">{sourceLabels[source] || source}</span>
                    </div>
                    <span className={`font-mono ${amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {amount >= 0 ? '+' : ''}{amount.toFixed(2)} MEL
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Transaction History */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                Transaction History
              </h2>
              {transactions.length > 5 && (
                <button 
                  onClick={() => fetchTransactions(1)}
                  className="text-zinc-400 text-sm hover:text-white flex items-center gap-1"
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((txn, index) => (
                  <motion.div
                    key={txn.transaction_id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800"
                    data-testid={`transaction-${txn.transaction_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        txn.transaction_type === 'spend'
                          ? 'bg-red-500/20 text-red-400'
                          : txn.status === 'confirmed' 
                          ? 'bg-green-500/20 text-green-400'
                          : txn.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        <TransactionSourceIcon source={txn.source} type={txn.transaction_type} />
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">{txn.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <TransactionStatusIcon status={txn.status} />
                          <span className="text-zinc-500 text-xs capitalize">{txn.status}</span>
                          <span className="text-zinc-600 text-xs">•</span>
                          <span className="text-zinc-500 text-xs">
                            {new Date(txn.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className={`font-mono text-lg ${
                      txn.status === 'forfeited' 
                        ? 'text-zinc-600 line-through'
                        : txn.transaction_type === 'earn'
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}>
                      {txn.transaction_type === 'earn' ? '+' : '-'}{txn.amount.toFixed(2)} MEL
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <img 
                  src={MELANIN_ICON} 
                  alt="Melanin" 
                  className="w-16 h-16 mx-auto mb-4 opacity-30"
                />
                <h3 className="text-lg font-medium text-white mb-2">No transactions yet</h3>
                <p className="text-zinc-500 text-sm">
                  Start earning Melanin by submitting businesses or writing reviews!
                </p>
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => fetchTransactions(page)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      pagination.page === page
                        ? 'bg-white text-black'
                        : 'bg-zinc-900 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </>
      )}

      {activeTab === 'account' && (
        <MelaninAccount 
          onConversionComplete={() => {
            refreshWallet();
          }}
        />
      )}

      {activeTab === 'buy' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-xl p-6"
        >
          <MelaninPurchase 
            onPurchaseComplete={(amount) => {
              refreshWallet();
              toast.success(`${amount} Melanin added to your wallet!`);
            }}
          />
        </motion.div>
      )}

      {activeTab === 'shop' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Boosts Section */}
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Boost Your Business
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Get your business featured on the homepage to increase visibility.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">7-Day Boost</h3>
                    <p className="text-zinc-400 text-sm">Featured for 1 week</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src={MELANIN_ICON} alt="" className="w-5 h-5" />
                    <span className="text-2xl font-bold text-white">2.00</span>
                    <span className="text-zinc-500">MEL</span>
                  </div>
                  <span className="text-zinc-500 text-sm">Purchase from your business</span>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">30-Day Boost</h3>
                    <p className="text-zinc-400 text-sm">Featured for 1 month</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src={MELANIN_ICON} alt="" className="w-5 h-5" />
                    <span className="text-2xl font-bold text-white">5.00</span>
                    <span className="text-zinc-500">MEL</span>
                  </div>
                  <span className="text-zinc-500 text-sm">Best value</span>
                </div>
              </div>
            </div>
          </div>

          {/* Badges Section */}
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Premium Badges
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Stand out with exclusive badges that show your commitment to excellence.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-1">Verified Plus</h3>
                <p className="text-zinc-500 text-xs mb-3">Enhanced verification badge</p>
                <div className="flex items-center justify-center gap-1">
                  <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                  <span className="font-bold text-white">3.00</span>
                  <span className="text-zinc-500 text-sm">MEL</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-3">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-1">Top Rated</h3>
                <p className="text-zinc-500 text-xs mb-3">For highly-rated businesses</p>
                <div className="flex items-center justify-center gap-1">
                  <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                  <span className="font-bold text-white">5.00</span>
                  <span className="text-zinc-500 text-sm">MEL</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-600 flex items-center justify-center mx-auto mb-3">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-1">Community Choice</h3>
                <p className="text-zinc-500 text-xs mb-3">Community favorite award</p>
                <div className="flex items-center justify-center gap-1">
                  <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                  <span className="font-bold text-white">10.00</span>
                  <span className="text-zinc-500 text-sm">MEL</span>
                </div>
              </div>
            </div>

            <p className="text-zinc-500 text-xs text-center mt-6">
              Purchase badges from your business dashboard after verification
            </p>
          </div>
        </motion.div>
      )}

      {activeTab === 'referrals' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Your Referral Code */}
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Your Referral Code
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Share your code with friends. You&apos;ll earn <span className="text-green-400 font-semibold">+1.00 MEL</span> for each friend who signs up!
            </p>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 mb-4">
              <span className="text-3xl font-mono font-bold text-white tracking-widest flex-1">
                {referralCode}
              </span>
              <button
                onClick={copyReferralCode}
                className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                data-testid="copy-referral-code"
              >
                <Copy className="w-5 h-5" />
              </button>
              <button
                onClick={copyReferralLink}
                className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                data-testid="share-referral-link"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Referral Stats */}
            {referralStats && (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <p className="text-zinc-500 text-sm">Total Referrals</p>
                  <p className="text-2xl font-bold text-white">{referralStats.total_referrals}</p>
                </div>
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <p className="text-zinc-500 text-sm">Earned from Referrals</p>
                  <p className="text-2xl font-bold text-green-400">+{referralStats.total_earnings?.toFixed(2) || '0.00'} MEL</p>
                </div>
              </div>
            )}
          </div>

          {/* Apply Referral Code */}
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Have a Referral Code?
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Enter a friend&apos;s code to receive <span className="text-green-400 font-semibold">+0.50 MEL</span> welcome bonus!
            </p>
            
            <div className="flex gap-3">
              <Input
                value={codeToApply}
                onChange={(e) => setCodeToApply(e.target.value.toUpperCase())}
                placeholder="Enter referral code"
                className="flex-1 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 font-mono tracking-widest"
                maxLength={8}
                data-testid="referral-code-input"
              />
              <button
                onClick={applyReferralCode}
                disabled={applyingCode}
                className="btn-primary px-6 disabled:opacity-50"
                data-testid="apply-referral-code"
              >
                {applyingCode ? 'Applying...' : 'Apply'}
              </button>
            </div>
          </div>

          {/* How it Works */}
          <div className="glass-card rounded-xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              How Referrals Work
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-medium text-white">Share Your Code</h3>
                  <p className="text-zinc-500 text-sm">Send your unique referral code to friends</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-medium text-white">They Sign Up</h3>
                  <p className="text-zinc-500 text-sm">Your friend creates an account and applies your code</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-medium text-white">Both Get Rewarded</h3>
                  <p className="text-zinc-500 text-sm">You get +1.00 MEL, they get +0.50 MEL</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
